import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Search } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["all", "organization", "mental health", "employment", "housing", "education", "legal"];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" } })
};

export default function ResourcesPage() {
  const [resources, setResources] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    axios.get(`${API}/resources`).then(r => setResources(r.data)).catch(() => {});
  }, []);

  const filtered = resources.filter(r =>
    (filter === "all" || r.category === filter) &&
    (search === "" || r.name.toLowerCase().includes(search.toLowerCase()) || r.description.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div data-testid="resources-page">
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/7467924/pexels-photo-7467924.jpeg?auto=compress&cs=tinysrgb&w=1600" alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Support</p>
          </motion.div>
          <motion.h1 initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] uppercase tracking-tighter leading-[0.9] mb-6">
            Veteran Resources
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={2}
            className="font-body text-base lg:text-lg text-zinc-400 max-w-xl leading-relaxed">
            Organizations and programs dedicated to supporting veterans and their families.
          </motion.p>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Search + Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input value={search} onChange={e => setSearch(e.target.value)} className="bc-input w-full pl-10" placeholder="Search resources..." data-testid="resource-search" />
            </div>
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map(cat => (
                <button key={cat} onClick={() => setFilter(cat)}
                  className={`font-subheading text-xs uppercase tracking-wider px-4 py-2 border transition-all ${filter === cat ? "border-white text-white" : "border-white/10 text-zinc-500 hover:border-white/30"}`}>
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20 border border-white/10">
              <p className="font-subheading text-lg uppercase tracking-wider text-zinc-500 mb-2">Resources Coming Soon</p>
              <p className="font-body text-sm text-zinc-600">We're compiling a comprehensive list of veteran organizations and programs. Check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((res, i) => (
                <motion.div key={res.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="border border-white/10 p-6 card-hover">
                  {res.logo_url && <img src={res.logo_url} alt={res.name} className="h-12 w-auto object-contain mb-4 opacity-70" />}
                  <h3 className="font-subheading text-lg uppercase tracking-wider mb-2">{res.name}</h3>
                  <span className="font-body text-[10px] uppercase tracking-[0.2em] text-zinc-600 border border-white/10 px-2 py-0.5 inline-block mb-3">{res.category}</span>
                  <p className="font-body text-sm text-zinc-500 mb-4 line-clamp-3">{res.description}</p>
                  {res.url && (
                    <a href={res.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-subheading text-xs uppercase tracking-widest text-zinc-400 hover:text-white transition-colors">
                      Visit <ExternalLink size={12} />
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
